/**
 * Biometria no navegador.
 *
 * Duas idas em cada cerimônia: pede as opções à API, entrega ao aparelho,
 * devolve o que ele assinou. Quem julga é o servidor — aqui não há decisão de
 * segurança nenhuma, só o transporte.
 *
 * O nome no produto é "Face ID ou digital", não "passkey": quem lê a tela é um
 * barbeiro entre dois cortes, e ele conhece o gesto pelo nome que o aparelho
 * dele usa.
 */

import type { PasskeyView } from '@barbearia/shared';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { api } from './api.ts';

/**
 * O aparelho consegue fazer biometria?
 *
 * `browserSupportsWebAuthn` é falso fora de contexto seguro — sem HTTPS o
 * navegador nem expõe a API —, e é por isso que a tela nunca mostra um botão
 * que não teria como funcionar. `platformAuthenticatorIsAvailable` separa o
 * Face ID do aparelho de uma chave USB, que este público não tem.
 */
export async function biometricsAvailable(): Promise<boolean> {
  if (!browserSupportsWebAuthn()) return false;

  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

/**
 * Quem desistiu no meio.
 *
 * Fechar o Face ID com o dedo — ou já ter uma passkey neste aparelho, que o
 * navegador barra pelo `excludeCredentials` — chega como `NotAllowedError` ou
 * `InvalidStateError`. Nenhum dos dois é defeito, e nenhum merece uma tarja
 * vermelha na tela.
 */
export function wasDismissed(error: unknown): boolean {
  return (
    error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'AbortError')
  );
}

/** O aparelho já tem uma passkey desta conta. */
export function alreadyRegistered(error: unknown): boolean {
  return error instanceof Error && error.name === 'InvalidStateError';
}

/** Registra este aparelho na conta que já está logada. */
export async function registerPasskey(): Promise<PasskeyView> {
  const { options } = await api.post<{ options: PublicKeyCredentialCreationOptionsJSON }>(
    '/auth/passkeys/register/options',
  );

  const credential = await startRegistration({ optionsJSON: options });

  const { passkey } = await api.post<{ passkey: PasskeyView }>('/auth/passkeys/register', {
    credential,
  });

  return passkey;
}

/**
 * Entra com o que o aparelho oferecer.
 *
 * Sem telefone, sem usuário: as credenciais são descobertas, então quem lista
 * as contas é o próprio aparelho. É esta chamada que existe atrás do botão
 * "Entrar com Face ID ou digital".
 */
export async function signInWithPasskey(): Promise<void> {
  const { options } = await api.post<{ options: PublicKeyCredentialRequestOptionsJSON }>(
    '/auth/passkeys/sign-in/options',
  );

  const credential = await startAuthentication({ optionsJSON: options });

  await api.post('/auth/passkeys/sign-in', { credential });
}
